import React, { Dispatch, SetStateAction, useEffect, useRef } from 'react';

import { faArrowRight, faClose } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert } from 'reactstrap';
import styled, { useTheme } from 'styled-components';

import infoIcon from 'images/info-icon.svg';
import warningIcon from 'images/warning-icon.svg';

import { UnstyledInnerHtml } from 'components/InnerHtml/InnerHtml';
import Link from 'components/Link';
import RawButton from 'components/RawButton/RawButton';

import useElementSize from 'hooks/useElementSize';

import { AlertBannerBlockI } from 'interfaces/content-stack/blocks/alertBanner';

import { getModularBlockLink } from 'utils/modularBlockUtils';

export const AlertSize = {
  lg: '3.1875rem',
  md: '5.875rem',
  sm: '4.625rem',
  xs: '6rem',
};

const TastyAlert = styled(Alert)`
  ${({ theme, maxHeight }) => `
    background-color: ${theme.colors.ttRed};
    margin-bottom: 0;
    border-radius: 0;
    border: none;
    padding: 0.1rem 1.25rem;
    position: sticky;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1050;
    max-width: 100vw;
    height: 100%;
    min-height: ${maxHeight}px;
    overflow: hidden;
  `}
`;

const Close = styled(FontAwesomeIcon)`
  color: ${(props) => props.theme.colors.white};
  cursor: pointer;
  position: absolute;
  top: 1.5rem;
  right: 0.5rem;
  transform: translateY(-50%);
  z-index: 1;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    top: 1rem;
  }
`;

const AlertIcon = styled.img`
  height: 1.25rem;
  margin-right: 1rem;
  margin-top: 0.2rem;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    margin-top: 0.3rem;
  }
`;

const StyledIconButton = styled(FontAwesomeIcon)`
  color: ${(props) => props.theme.colors.white};
  margin-left: 0.5rem;
`;

const AlertContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  padding-right: 2rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.xl}) {
    flex-direction: column;
    align-items: normal;
  }

  @media (min-width: ${(props) => props.theme.breakpoints.xl}) {
    flex-direction: row;
    justify-content: space-between;
    padding: 0 40px;
  }
`;

const Title = styled.div`
  font-size: 1.125rem;
  color: ${(props) => props.theme.colors.white};
  margin-bottom: 0;
  margin-right: 1rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: 0.875rem;
  }

  p {
    margin: 0;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    span {
      width: 95%;
      display: block;
    }
  }
`;

const StyledLink = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    margin-left: 2.25rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.xl}) {
    margin-left: 2.25rem;
  }
`;

const IconTainer = styled.div`
  display: flex;

  @media (max-width: ${({ theme }) => theme.breakpoints.xs}) {
    padding-top: 0.5rem;
  }

  @media (min-width: ${(props) => props.theme.breakpoints.md}) {
    align-items: flex-start;
  }
`;

const AlertCta = styled.span`
  color: ${(props) => props.theme.colors.white};
  font-size: 0.8rem !important;
  text-decoration: none;
  text-transform: uppercase;
  white-space: nowrap;
  margin-bottom: 0;
  line-height: 3rem;
  font-family: ${(props) => props.theme.fonts.fontFamilySecondary};

  &:hover {
    color: ${(props) => props.theme.colors.white};
    text-decoration: none;
  }

  svg {
    margin-left: 0.5rem;
  }
`;

export interface AlertBannerProps {
  data: AlertBannerBlockI;
  type?: string;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  setAlertHeight: Dispatch<SetStateAction<number>>;
}

function AlertBanner({ data, type = 'warning', isOpen, setIsOpen, setAlertHeight }: AlertBannerProps) {
  const theme = useTheme();
  const alertRef = useRef(null);
  const size = useElementSize(alertRef);

  useEffect(() => {
    if (size.height != null) {
      setAlertHeight(size.height);
    }
    return () => {
      setAlertHeight(0);
    };
  }, [size]);

  const toggleAlert = () => {
    setIsOpen(!isOpen);
    sessionStorage.setItem('closedAlert', 'true');
  };

  const alertButton = data?.siteAlerts?.alertButton;
  const buttonLink = getModularBlockLink(alertButton?.buttonLink);
  const buttonText = alertButton?.buttonText;

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const currentPath = window.location.pathname;
      const alertLink = buttonLink ?? '';

      if (currentPath === alertLink || currentPath === `${alertLink}/`) {
        toggleAlert();
      }
    }
  }, [buttonLink]);

  if (!isOpen) {
    return null;
  }

  return (
    <>
      {data.siteAlerts.isActive && (
        <TastyAlert isOpen={isOpen} maxHeight={size.height ? size.height + 3 : 51}>
          <AlertContainer ref={alertRef}>
            <IconTainer>
              <AlertIcon alt={`${type} icon`} src={type === 'info' ? infoIcon : warningIcon} />
              <Title>
                <UnstyledInnerHtml richText={data.siteAlerts.richTextEditor.replaceAll(/<\/?p>/g, '')} />
              </Title>
            </IconTainer>
            <StyledLink activeStyle={{ backgroundColor: theme.colors.ttRed }} to={buttonLink ?? ''}>
              {alertButton && <AlertCta>{buttonText ?? 'Learn More'}</AlertCta>}
              <StyledIconButton icon={faArrowRight} />
            </StyledLink>
            <RawButton aria-label='close' type='button'>
              <Close icon={faClose} onClick={toggleAlert} data-testid='closeAlert' />
            </RawButton>
          </AlertContainer>
        </TastyAlert>
      )}
    </>
  );
}

export default AlertBanner;
